/**
 * Проверка доступа по правам (DEV-1257, ADR-0042).
 *
 * До реформы здесь жила ролевая модель: 7 флагов вида isAdmin/isHos и 20
 * производных от них canXxx. Роль — не источник истины о доступе: бэкенд
 * пускает по праву `resource:action`, и совпадение множеств держалось на
 * договорённости, которая расходилась при каждой новой роли (ADMIN_USER
 * фронту не была известна вовсе).
 *
 * Теперь основа — `can(permission)` поверх эффективных прав из /me. Право
 * здесь означает ровно то же, что на бэкенде, поэтому спрятанная кнопка и
 * отказ сервера больше не могут разойтись.
 *
 * DEV-1298 закрыл последний разрыв: гейты, которые сравнивали строки
 * "admin"/"hos"/"ceo" внутри deals, client-data, analytics и
 * messenger-providers, теперь спрашивают права, и здесь стоят ровно те же
 * имена. Роль осталась в одном месте — `isSupportManager`, где она означает
 * тип сотрудника, а не разрешение (пояснение ниже, у самого флага).
 */
import { computed } from 'vue'

import { useSectionsStore } from '../stores/sections.store'
import type { Permission } from '../sections/permissions.generated'
import type { UserRole } from '../types/auth.types'
import { useAuth } from './useAuth'

export const usePermissions = () => {
  const { user } = useAuth()
  const sections = useSectionsStore()

  /** Есть ли право. В legacy-режиме (бэкенд без карты) разрешает всё. */
  const can = (permission: Permission): boolean => sections.hasPermission(permission)

  /** Есть ли хотя бы одно из прав. */
  const canAny = (...permissions: Permission[]): boolean => permissions.some(can)

  // Роли приходят в ВЕРХНЕМ регистре — сравнение только регистронезависимое,
  // иначе проверка молча становится всегда-ложной (так умерла модерация
  // комментариев в DealCommentsSection).
  const hasAnyRole = (...roles: UserRole[]): boolean => {
    const mine = (user.value?.roles ?? []).map((r) => r.toLowerCase())
    return roles.some((role) => mine.includes(role.toLowerCase() as UserRole))
  }

  // ——— Клиенты ———
  const canAccessClients = computed(() => can('clients:read'))
  const canCreateClients = computed(() => can('clients:create'))
  const canEditClients = computed(() => can('clients:update'))
  /**
   * DEV-1918. Канал привлечения и агент на карточке клиента: ровно то право,
   * которым client-data гейтит client_source_type, а agents — SetClientAgent.
   */
  const canAssignAgent = computed(() => can('clients:assign_agent'))
  // ——— Сделки ———
  const canAccessDeals = computed(() => can('deals:read'))
  const canCreateDeals = computed(() => can('deals:create'))
  const canEditDeals = computed(() => can('deals:update'))

  // ——— Объекты ———
  const canAccessProperties = computed(() => can('properties:read'))
  // DEV-1082/DEV-1080. Конструктор проекта: форма, юниты, блоки — всё про запись.
  const canManageProperties = computed(() => canAny('properties:create', 'properties:update'))
  // DEV-1003. Тумблер доступности ЖК агентам — правка самого объекта.
  const canManageAgentAvailability = computed(() => can('properties:update'))

  // ——— Агенты и партнёры ———
  const canAccessAgents = computed(() => can('agents:read'))
  const canManageAgents = computed(() => can('agents:manage'))
  // DEV-795. Очередь приёма компаний-партнёров.
  const canModerateCompanyVerifications = computed(() => can('partner_companies:update'))

  // ——— Календарь ———
  /**
   * Командный календарь: чужие события. Ровно то право, которое проверяет
   * calendar-сервис, — фильтр менеджеров и ответ сервера теперь совпадают.
   */
  const canViewAllCalendars = computed(() => can('calendar:read_all'))

  // ——— Конструкторы ———
  const canConfigurePipeline = computed(() => can('pipeline:update'))

  // ——— Руководящий контур (DEV-1298: переведён с ролей на права) ———

  /**
   * Руководящие действия в сделках: колонка «Неразобранное» и сделки чужих
   * менеджеров, правка описания и срока задачи (DEV-196), состав команды
   * сделки, история менеджеров, смена ответственного (DEV-715).
   *
   * Ровно то право, которое проверяет deals в service-слое, — спрятанная
   * кнопка и отказ сервера больше не могут разойтись.
   */
  const canSuperviseDeals = computed(() => can('deals:manage'))

  /** DEV-715. Смена ответственного менеджера у клиента и у сделки. */
  const canManageResponsibleManager = canSuperviseDeals

  /**
   * Архивирование карточки клиента с указанием причины.
   *
   * Отдельное право, а не `clients:update`: архивация уезжает тем же
   * UpdateClient, и раньше ограничение до руководителей было ширмой — сервер
   * принимал запрос от любого менеджера. Теперь client-data принуждает
   * `clients:archive`, и UI повторяет ровно его.
   */
  const canArchiveClients = computed(() => can('clients:archive'))

  /**
   * Настройки выгрузки отчёта продаж: куда уезжают данные компании. Гейт в
   * analytics намеренно уже, чем чтение отчёта, — `deals:manage`, не
   * `reports:read_all`.
   */
  const canExportSalesReport = canSuperviseDeals

  /**
   * Справочник менеджеров: фильтры по ответственному и выбор при создании.
   * client-data отдаёт полный список по `users:read`.
   */
  const canSeeManagerDirectory = computed(() => can('users:read'))

  /**
   * DEV-197. Аналитика менеджеров: срез по чужим результатам. Одно право на
   * analytics и на выгрузку в deals.
   */
  const canAccessManagerAnalytics = computed(() => can('reports:read_all'))

  /** DEV-998. Переписка других менеджеров с клиентом (messenger-providers). */
  const canViewOtherManagerChats = computed(() => can('communications:read_all'))

  /**
   * DEV-1833. Отправка от имени поддержки: ровно то право, которым
   * messenger-providers гейтит target_manager_id="support" в send/markAsRead/react.
   */
  const canWriteAsSupport = computed(() => can('communications:write_all'))

  /**
   * DEV-1712. Общий (support) аккаунт мессенджеров. Ровно то право, которым
   * messenger-providers гейтит все четыре support-RPC Telegram: StartLogin,
   * GetStatus, SubmitPassword и Logout.
   */
  const canManageMessengers = computed(() => can('messengers:manage'))

  /**
   * DEV-2418. Звонки на общий номер WhatsApp Business: принимать и отклонять —
   * ровно то право, которым messenger-providers гейтит WABA_CALL_ACCEPT/DECLINE.
   * Оно у ролей, работающих с клиентами; у маркетинга и агентов его нет.
   */
  const canAnswerWabaCalls = computed(() => can('communications:create'))

  /**
   * DEV-1670. Писать с общего номера WhatsApp Business (текст, файлы, шаблон,
   * галочки прочтения) — то же право, которым messenger-providers гейтит запись.
   * Читать переписку можно и без него, если видна карточка клиента.
   */
  const canWriteWabaChats = computed(() => can('communications:create'))

  /** DEV-2418. История звонков общего номера с записями — как записи телефонии. */
  const canReadWabaCallHistory = computed(() => can('telephony:read'))

  /**
   * DEV-2018 (эпик DEV-2005). Видеовстречи с клиентом.
   *
   * ⚠ Записи и расшифровки живут под отдельным правом: содержание разговора
   * узкее, чем факт встречи, и его слышат не все, кто эту встречу видит.
   */
  const canManageMeetings = computed(() => can('meetings:manage'))
  const canReadMeetings = computed(() => can('meetings:read'))
  const canReadMeetingRecordings = computed(() => can('meetings:read_recordings'))

  /**
   * DEV-699. Сопровождающий менеджер: сделки ведёт, но не заводит.
   *
   * ⚠️ Единственная оставшаяся проверка по роли, и это не недоделка. Право
   * `deals:create` у роли SUPPORT_MANAGER снять нельзя: CRUD-экстрактор deals
   * мапит по префиксу имени метода, поэтому тем же правом гейтятся `CreateTask`
   * и `CreateDealComment` — задачи и комментарии по сопровождаемой сделке.
   * Снятие гранта сломало бы обе функции ради запрета, который service-слой и
   * так соблюдает.
   *
   * По сути роль здесь означает не разрешение, а тип сотрудника — как и в
   * deals, где ветка `support_manager` выбирает, по какому полю считать «свои»
   * сделки. Такие места правом не выражаются.
   */
  const isSupportManager = computed(() => hasAnyRole('support_manager'))

  /**
   * Конструктор ролей. Роль, а не право, НАМЕРЕННО: раздавать право на раздачу
   * прав через ту же модель — рекурсия, поэтому CreateRole/UpdateRole/DeleteRole
   * гейтятся на сервере ролью ADMIN (`authz_policy.go`). Здесь стоит ровно та же
   * проверка, чтобы кнопка и отказ сервера не разошлись (DEV-1268).
   */
  const canEditRoleModel = computed(() => hasAnyRole('admin'))

  return {
    can,
    canAny,

    canAccessClients,
    canCreateClients,
    canEditClients,
    canAssignAgent,

    canAccessDeals,
    canCreateDeals,
    canEditDeals,

    canAccessProperties,
    canManageProperties,
    canManageAgentAvailability,

    canAccessAgents,
    canManageAgents,
    canModerateCompanyVerifications,

    canViewAllCalendars,
    canConfigurePipeline,

    canSuperviseDeals,
    canSeeManagerDirectory,
    canManageResponsibleManager,
    canArchiveClients,
    canExportSalesReport,
    canAccessManagerAnalytics,
    canViewOtherManagerChats,
    canWriteAsSupport,
    canManageMessengers,
    canAnswerWabaCalls,
    canWriteWabaChats,
    canReadWabaCallHistory,
    canManageMeetings,
    canReadMeetings,
    canReadMeetingRecordings,
    canEditRoleModel,
    isSupportManager,
  }
}
